/**
 * Deciding a contested book.
 *
 * The screen exists to make a call defensible to the person who loses it, so it
 * shows the ordered evidence with the millisecond clock rather than only the
 * verdict. The earliest claim is pre-highlighted with the halo; awarding it is
 * one tap, and awarding anyone else is deliberately the same one tap so the
 * owner is never fighting the UI to make a judgement call.
 */
export interface ClaimEvidence {
  claimId: string
  claimant: string
  group: string
  claimedAt: string
  rank: number
}

export interface AdjudicationProps {
  unit: { unitId: string; title: string }
  claims: ClaimEvidence[]
}

/** Clock time to the millisecond - the ms is the whole point. */
function clockOf(iso: string): string {
  return iso.slice(11, 23)
}

function gapWords(claims: ClaimEvidence[]): string {
  if (claims.length < 2) return 'the only claim'
  const ms = Date.parse(claims[1].claimedAt) - Date.parse(claims[0].claimedAt)
  return `first by ${ms} ms`
}

export function Adjudication({ unit, claims }: AdjudicationProps) {
  const ordered = [...claims].sort((a, b) => a.rank - b.rank)
  return (
    <div className="adjudication">
      <header className="station-header">
        <h1 className="t-display">Contested</h1>
        <p className="t-title">{unit.title}</p>
        <p className="t-scan">{unit.unitId}</p>
      </header>

      <ul className="claim-list">
        {ordered.map((c, i) => (
          <li key={c.claimId} className={`claim-row${i === 0 ? ' pulse-halo claim-leader' : ''}`}>
            <span className="claim-rank">{c.rank}</span>
            <span className="claim-name">{c.claimant}</span>
            <span className="claim-group muted">{c.group}</span>
            <span className="t-scan claim-clock">{clockOf(c.claimedAt)}</span>
            <button type="button" className="btn btn-award">
              Award
            </button>
          </li>
        ))}
      </ul>

      <p className="why-defensible">
        {ordered[0]?.claimant} was {gapWords(ordered)}.
      </p>
    </div>
  )
}
